import { useCallback, useEffect, useRef, useState } from "react";
import type { MapView } from "@/lib/core/schemas/map-view";

/** Typed error so the consumer can switch on auth vs other failures. */
export class MapAuthError extends Error {
  constructor(message = "Map view auth failed") {
    super(message);
    this.name = "MapAuthError";
  }
}

export class MapForbiddenError extends Error {
  constructor(message = "Map view forbidden") {
    super(message);
    this.name = "MapForbiddenError";
  }
}

export class MapNotFoundError extends Error {
  constructor(message = "Campaign not found") {
    super(message);
    this.name = "MapNotFoundError";
  }
}

export type UseMapViewResult = {
  data: MapView | null;
  error: Error | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

export const useMapView = (campaignId: string): UseMapViewResult => {
  const [data, setData] = useState<MapView | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const inflight = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    inflight.current?.abort();
    const controller = new AbortController();
    inflight.current = controller;
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/campaigns/${campaignId}/map`, {
        signal: controller.signal,
        credentials: "include",
      });

      if (res.status === 401) {
        setData(null);
        setError(new MapAuthError());
        return;
      }
      if (res.status === 403) {
        setError(new MapForbiddenError());
        return;
      }
      if (res.status === 404) {
        setError(new MapNotFoundError());
        return;
      }

      if (!res.ok) {
        throw new Error(`Map fetch failed: ${res.status} ${res.statusText}`);
      }

      const body = (await res.json()) as MapView;
      setData(body);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      setError(err instanceof Error ? err : new Error(String(err)));
      // Stale data deliberately retained for non-auth failures
    } finally {
      if (inflight.current === controller) {
        setIsLoading(false);
        inflight.current = null;
      }
    }
  }, [campaignId]);

  useEffect(() => {
    load().catch(() => {
      // errors are handled inside load() via setError; this catch suppresses unhandled-promise warnings
    });
    return () => inflight.current?.abort();
  }, [load]);

  return { data, error, isLoading, refresh: load };
};
